import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Eye, EyeOff, X, Settings, Users, GraduationCap, Download } from 'lucide-react';
import ReportService from '../services/ReportService';
import Table from '../components/ui/Table';
import Modal from '../components/ui/Modal';
import ConfirmModal from '../components/ui/ConfirmModal';
import SearchableDropdown from '../components/SearchableDropdown';
import { useToast } from '../contexts/ToastContext';
import { useSelector } from 'react-redux';
import APIRequest from '../api/APIRequest';
import { staffService } from '../services/staffService';
import { classService } from '../services/classService';
import { designationService } from '../services/designationService';
import { fieldConfigService } from '../services/fieldConfigService';
import DynamicFields from '../components/forms/DynamicFields';
import CustomDatePicker from '../components/ui/CustomDatePicker';
import DesignationSettingsModal from '../components/DesignationSettingsModal';
import PhoneInput from '../components/ui/PhoneInput';
import ItemsPerPageSelector from '../components/ui/ItemsPerPageSelector';



const StaffAvatar = ({ teacher }) => {
  const [imgError, setImgError] = useState(false);
  const initials = teacher.name.split(' ').map(n => n[0]).join('').slice(0, 2);

  return (
    <div className="relative group">
      <div className="w-12 h-12 primary-gradient-bg rounded-2xl flex items-center justify-center overflow-hidden premium-shadow group-hover:scale-105 transition-transform duration-300">
        {teacher.image && !imgError ? (
          <img
            src={`/${teacher.image}`}
            alt={teacher.name}
            className="w-full h-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <span className="text-white font-bold text-lg uppercase tracking-wider">
            {initials}
          </span>
        )}
      </div>
      <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 border-2 border-white rounded-full"></div>
    </div>
  );
};

const Staff = () => {
  const navigate = useNavigate();
  const { error, success } = useToast();
  const { user } = useSelector((state) => state.auth);
  const [teachers, setTeachers] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [isDesignationModalOpen, setIsDesignationModalOpen] = useState(false);
  const [classes, setClasses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const saved = localStorage.getItem('staffItemsPerPage');
    return saved ? JSON.parse(saved) : 10;
  });

  const handleItemsPerPageChange = (newValue) => {
    setItemsPerPage(newValue);
    setCurrentPage(1); // Reset to first page
    localStorage.setItem('staffItemsPerPage', JSON.stringify(newValue));
  };
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewMode] = useState('list');
  const [isCreating, setIsCreating] = useState(false);
  const [configFields, setConfigFields] = useState([]); // Fields for selected designation
  const [allConfigFields, setAllConfigFields] = useState([]); // All fields including system fields

  // Filter states
  const [nameSort, setNameSort] = useState('a-z'); // 'a-z' or 'z-a'
  const [selectedClassInchargeFilter, setSelectedClassInchargeFilter] = useState('');
  const [selectedClassInchargeOfFilter, setSelectedClassInchargeOfFilter] = useState('');
  const [selectedGenderFilter, setSelectedGenderFilter] = useState('');
  const [selectedDesignationFilter, setSelectedDesignationFilter] = useState('');
  const [selectedAcademicYearFilter, setSelectedAcademicYearFilter] = useState('');

  // Modal protection states
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [historyStateAdded, setHistoryStateAdded] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [staffToToggle, setStaffToToggle] = useState(null);
  const [targetStatus, setTargetStatus] = useState('');

  const handleResetColumns = () => {
    // Hidden
  };

  // Utility function to format currency
  // const formatCurrency = (amount) => {
  //   if (!amount || isNaN(amount)) return '₹0';
  //   return `₹${parseFloat(amount).toLocaleString('en-IN')}`;
  // };


  const [formData, setFormData] = useState({
    name: '',
    designation: '',
    qualification: '',
    experienceInYears: '',
    email: '',
    phone: '',
    class: '',
    dateOfBirth: '',
    contactPerson: [{ relation: '', phone: '', name: '' }],
    maritalStatus: 'single',
    gender: 'male',
    motherTongue: '',
    nationality: '',
    subjects: [{ name: '' }],
    aadharNumber: '',
    pan: '',
    bloodGroup: '',
    address: '',
    salary: '',
    academicYear: '',
    inChargeOf: '',
    image: null,
    password: '',
    customFields: {}
  });

  // Function to check if form has unsaved changes
  const hasUnsavedChanges = React.useCallback(() => {
    const initialFormData = {
      name: '',
      designation: '',
      qualification: '',
      experienceInYears: 0,
      email: '',
      phone: '',
      dateOfBirth: '',
      contactPerson: [{ relation: '', phone: '', name: '' }],
      maritalStatus: 'single',
      gender: 'male',
      motherTongue: '',
      nationality: '',
      subjects: [{ name: '' }],
      aadharNumber: '',
      pan: '',
      bloodGroup: '',
      address: '',
      salary: '',
      academicYear: ''
    };
    return JSON.stringify(formData) !== JSON.stringify(initialFormData);
  }, [formData]);

  useEffect(() => {
    (async () => {
      const [teachersData, classesData, designationsResponse] = await Promise.all([
        staffService.getAllTeachers(user.token),
        classService.getAllClasses(user.token),
        designationService.getAllDesignations(user.token)
      ]);
      setTeachers(Array.isArray(teachersData) ? teachersData : (teachersData?.teachers || []));
      setClasses(Array.isArray(classesData) ? classesData : (classesData?.classes || []));
      
      // Robust designation response handling
      let designationsData = [];
      if (Array.isArray(designationsResponse)) {
        designationsData = designationsResponse;
      } else if (designationsResponse?.data && Array.isArray(designationsResponse.data)) {
        designationsData = designationsResponse.data;
      } else if (designationsResponse?.designations && Array.isArray(designationsResponse.designations)) {
        designationsData = designationsResponse.designations;
      } else if (typeof designationsResponse === 'object' && designationsResponse !== null) {
        // Fallback for any other object structure that might contain the array
        designationsData = Object.values(designationsResponse).find(Array.isArray) || [];
      }

      setDesignations(designationsData);
    })();
  }, [user.token]);

  // No auto-initialization - show all teachers by default

  // Handle page refresh with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isModalOpen && hasUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return 'You have unsaved changes. Are you sure you want to leave?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isModalOpen, hasUnsavedChanges]);

  const getPasswordPattern = () => {
    const contact = formData.contactPerson?.[0];
    const dob = formData.dateOfBirth;
    const phone = formData.phone;

    if (!contact?.name || !dob || !phone) {
      return null;
    }

    const firstTwo = contact.name.trim().substring(0, 2).toUpperCase();
    const [year, month, day] = dob.split('-');
    const formattedDob = `${year}${month}${day}`;
    const cleanedPhone = phone.trim().replace(/\s/g, '');
    const lastTwoPhone = cleanedPhone.slice(-2);

    return `${firstTwo}@${formattedDob}${lastTwoPhone}`;
  };

  const generatePassword = () => {
    const generated = getPasswordPattern();
    if (!generated) {
      error('Please fill Emergency Contact Name, Date of Birth, and Phone first');
      return;
    }
    setFormData(prev => ({ ...prev, password: generated }));
    success('Password generated: ' + generated);
  };


  // Add history state when modal opens
  useEffect(() => {
    if (isModalOpen && !historyStateAdded) {
      window.history.pushState({ modalOpen: true }, '', window.location.href);
      setHistoryStateAdded(true);
    } else if (!isModalOpen && historyStateAdded) {
      setHistoryStateAdded(false);
    }
  }, [isModalOpen, historyStateAdded]);

  // Handle back button navigation with unsaved changes
  useEffect(() => {
    const handlePopState = (e) => {
      if (isModalOpen) {
        if (hasUnsavedChanges()) {
          // Show our custom confirmation modal
          setPendingAction('back');
          setShowUnsavedChangesModal(true);
          // Push the state back to prevent navigation
          window.history.pushState({ modalOpen: true }, '', window.location.href);
        } else {
          // If no unsaved changes, allow navigation by closing modal
          setIsModalOpen(false);
          resetForm();
        }
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isModalOpen, hasUnsavedChanges]);

  // Get unique academic years from classes
  const getUniqueAcademicYears = () => {
    const academicYears = new Set();
    classes.forEach(cls => {
      if (cls.academicYearFrom && cls.academicYearTo) {
        const fromYear = cls.academicYearFrom.toString().length <= 4 
          ? cls.academicYearFrom 
          : new Date(cls.academicYearFrom).getFullYear();
        const toYear = cls.academicYearTo.toString().length <= 4 
          ? cls.academicYearTo 
          : new Date(cls.academicYearTo).getFullYear();
        academicYears.add(`${fromYear}-${toYear}`);
      }
    });
    return Array.from(academicYears).sort();
  };

  // Filter classes based on selected academic year
  const getFilteredClasses = () => {
    if (!formData.academicYear) return classes;

    return classes.filter(cls => {
      if (!cls.academicYearFrom || !cls.academicYearTo) return false;

      const fromYear = cls.academicYearFrom.toString().length <= 4 
        ? cls.academicYearFrom 
        : new Date(cls.academicYearFrom).getFullYear();
      const toYear = cls.academicYearTo.toString().length <= 4 
        ? cls.academicYearTo 
        : new Date(cls.academicYearTo).getFullYear();
      const classAcademicYear = `${fromYear}-${toYear}`;

      if (classAcademicYear !== formData.academicYear) return false;

      // Filter: Show only classes without an in-charge teacher
      return !cls.classIncharge;
    });
  };

  // Create class options for dropdown based on filtered classes
  const classOptions = getFilteredClasses().map(cls => ({
    _id: cls._id,
    name: `${cls.name} - Section ${cls.section}`,
    subtitle: `Capacity: ${cls.class_capacity}`
  }));

  // Get classes filtered by selected academic year for main view
  const getClassesForSelectedAcademicYear = () => {
    if (!selectedAcademicYearFilter) return classes;

    return classes.filter(cls => {
      if (!cls.academicYearFrom || !cls.academicYearTo) return false;
      const fromYear = new Date(cls.academicYearFrom).getFullYear();
      const toYear = new Date(cls.academicYearTo).getFullYear();
      const academicYear = `${fromYear}-${toYear}`;
      return academicYear === selectedAcademicYearFilter;
    });
  };

  const filteredTeachers = teachers.filter(teacher => {
    // Text search filter
    const matchesSearch = !searchTerm ||
      teacher?.name?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      teacher?.email?.toLowerCase().includes(searchTerm?.toLowerCase());

    // Class incharge filter (Yes/No/All)
    const isClassIncharge = classes.some(cls => cls.classIncharge === teacher._id);
    const matchesClassIncharge = !selectedClassInchargeFilter ||
      (selectedClassInchargeFilter === 'yes' && isClassIncharge) ||
      (selectedClassInchargeFilter === 'no' && !isClassIncharge);

    // Class incharge of specific class filter
    const isInchargeOfSpecificClass = selectedClassInchargeOfFilter ?
      classes.some(cls => cls._id === selectedClassInchargeOfFilter && cls.classIncharge === teacher._id) : true;

    // Gender filter
    const matchesGender = !selectedGenderFilter ||
      teacher?.gender?.toLowerCase() === selectedGenderFilter.toLowerCase();

    // Academic year filter - show all teachers when academic year is selected
    // Note: We're not filtering by academic year for teachers since teachers aren't directly
    // assigned to academic years in the current data model. Keeping filter for UI consistency.
    const matchesAcademicYear = true;
    // Filter by designation
if (selectedDesignationFilter) {
  if (teacher.designation !== selectedDesignationFilter) {
    return false;
  }
}


    return matchesSearch && matchesClassIncharge && isInchargeOfSpecificClass && matchesGender && matchesAcademicYear;
  }).sort((a, b) => {
    // Name sorting
    if (nameSort === 'a-z') {
      return (a?.name || '').localeCompare(b?.name || '');
    } else {
      return (b?.name || '').localeCompare(a?.name || '');
    }
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredTeachers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTeachers = filteredTeachers.slice(startIndex, startIndex + itemsPerPage);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedClassInchargeFilter, selectedClassInchargeOfFilter, selectedGenderFilter, selectedAcademicYearFilter, nameSort]);

  // Fetch custom fields when designation changes
  useEffect(() => {
    const fetchFields = async () => {
      if (!formData.designation || designations.length === 0) {
        setConfigFields([]);
        return;
      }
      
      const selectedDesignation = designations.find(d => d.name === formData.designation);
      if (selectedDesignation) {
        try {
          const response = await fieldConfigService.getDesignationFields(selectedDesignation._id, user.token);
          const fields = Array.isArray(response) ? response : (response.data || []);
          setAllConfigFields(fields);
          // Filter out fields that are already handled manually in the specialized form sections
          const systemFields = [
            'name', 'email', 'phone', 'dateOfBirth', 'gender', 'maritalStatus', 
            'motherTongue', 'nationality', 'aadharNumber', 'pan', 'bloodGroup', 
            'address', 'salary', 'contactPerson', 'emergencyContacts', 'subjects',
            'class', 'academicYear', 'inChargeOf'
          ];
          const filteredFields = Array.isArray(fields) 
            ? fields.filter(f => !systemFields.includes(f.fieldName)) 
            : [];
          setConfigFields(filteredFields);
        } catch (err) {
          console.error('Error fetching fields for designation:', err);
          setConfigFields([]);
        }
      } else {
        setConfigFields([]);
      }
    };
    fetchFields();
  }, [formData.designation, designations, user.token]);

  const handleCustomFieldChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      customFields: {
        ...prev.customFields,
        [name]: value
      }
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      designation: '',
      qualification: '',
      experienceInYears: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      contactPerson: [{ relation: '', phone: '', name: '' }],
      maritalStatus: 'single',
      gender: 'male',
      motherTongue: '',
      nationality: '',
      subjects: [{ name: '' }],
      aadharNumber: '',
      pan: '',
      bloodGroup: '',
      address: '',
      salary: '',
      academicYear: '',
      class: '',
      inChargeOf: '',
      image: null,
      password: '',
      employeeNo: '',
      customFields: {}
    });
    setImagePreview(null);
    setValidationErrors({});
    setConfigFields([]);
  };



  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      error('Please fix the errors in the form before submitting.');
      return;
    }

    setIsCreating(true);

    let finalPassword = formData.password;
    if (!finalPassword) {
      finalPassword = getPasswordPattern();
      if (!finalPassword) {
        error('Please fill Emergency Contact Name, Date of Birth, and Phone for auto-password generation, or enter a password manually.');
        return;
      }
    }

    const api = new APIRequest();
    const payload = new FormData();

    payload.append('name', formData.name);
    payload.append('designation', formData.designation);
    
    // Consolidate qualification and experience (check both root and customFields)
    const qualification = formData.qualification || formData.customFields?.qualification || '';
    const experienceInYears = (formData.experienceInYears !== '' && formData.experienceInYears !== undefined) 
      ? formData.experienceInYears 
      : (formData.customFields?.experienceInYears || 0);

    payload.append('qualification', qualification);
    payload.append('experienceInYears', experienceInYears);
    payload.append('email', formData.email);
    payload.append('phone', formData.phone || '');
    payload.append('dateOfBirth', formData.dateOfBirth);
    payload.append('contactPerson', JSON.stringify(formData.contactPerson));
    payload.append('maritalStatus', formData.maritalStatus);
    payload.append('gender', formData.gender);
    payload.append('motherTongue', formData.motherTongue);
    payload.append('nationality', formData.nationality);
    payload.append('subjects', JSON.stringify(formData.subjects));
    payload.append('aadharNumber', formData.aadharNumber);
    payload.append('pan', formData.pan);
    payload.append('bloodGroup', formData.bloodGroup);
    payload.append('address', formData.address);
    payload.append('salary', formData.salary);
    payload.append('salary', formData.salary);
    payload.append('role', 'staff');
    payload.append('academicYear', formData.academicYear);
    payload.append('password', finalPassword);
    payload.append('inChargeOf', formData.inChargeOf?._id || '');
    payload.append('customFields', JSON.stringify(formData.customFields));

    if (formData.image) {
      payload.append('image', formData.image);
    }
    try {
      const response = await api.request('/admin/createTeacher', 'POST', payload, user.token);
      console.log(response, 'response');
      if (response.success) {
        success(response.message || 'Staff added successfully!');
        setIsModalOpen(false);
        resetForm();
        const updatedTeachers = await staffService.getAllTeachers(user.token);
        setTeachers(updatedTeachers);
      } else {
        error('Failed to add staff. Please try again.');
      }
    } catch (err) {
      console.error('Error adding teacher:', err);
      error('Failed to add staff. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleView = async (teacher) => {
    setIsCreating(true);
    try {
      const api = new APIRequest();
      const response = await api.request(`/admin/getTeacher?id=${teacher._id}`, 'GET', {}, user.token);
      console.log(response, 'response');
      if (response.success) {
        navigate(`/staff/${teacher._id}`, { state: { teacherData: response.teacher } });
      } else {
        error(response.message || 'Failed to fetch teacher details. Please try again.');
      }
    } catch (err) {
      console.error('Error fetching teacher data:', err);
      error('Failed to fetch teacher details. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleEdit = async (teacher) => {
    setIsCreating(true);
    try {
      const api = new APIRequest();
      const response = await api.request(`/admin/getTeacher?id=${teacher._id}`, 'GET', {}, user.token);
      if (response.success) {
        navigate(`/staff/${teacher._id}/edit`, { state: { teacherData: response.teacher } });
      } else {
        error(response.message || 'Failed to fetch teacher details. Please try again.');
      }
    } catch (err) {
      console.error('Error fetching teacher data:', err);
      error('Failed to fetch teacher details. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this teacher?')) {
      try {
        const response = await staffService.deleteTeacher(id, user.token);
        if (response.success) {
          setTeachers(teachers.filter(t => t._id !== id));
          success('Teacher deleted successfully');
        } else {
          error(response.message || 'Failed to delete teacher');
        }
      } catch (err) {
        console.error('Error deleting teacher:', err);
        error('Failed to delete teacher');
      }
    }
  };


  const addContactPerson = () => {
    setFormData({
      ...formData,
      contactPerson: [...(formData.contactPerson || []), { relation: '', phone: '', name: '' }]
    });
  };

  const removeContactPerson = (index) => {
    const updated = formData.contactPerson?.filter((_, i) => i !== index) || [];
    setFormData({ ...formData, contactPerson: updated });
  };

  const updateContactPerson = (index, field, value) => {
    const updated = formData.contactPerson?.map((cp, i) =>
      i === index ? { ...cp, [field]: value } : cp
    ) || [];
    setFormData({ ...formData, contactPerson: updated });
  };


  const addSubject = () => {
    setFormData({
      ...formData,
      subjects: [...(formData.subjects || []), { name: '' }]
    });
  };

  const handleStatusToggle = async () => {
    if (!staffToToggle) return;

    try {
      const api = new APIRequest();
      const response = await api.request(`/admin/updateTeacherStatus?id=${staffToToggle._id}`, 'POST', {
        status: targetStatus
      }, user.token);

      if (response.success) {
        setTeachers(teachers.map(t => 
          (t._id === staffToToggle._id) ? { ...t, status: targetStatus } : t
        ));
        success(`Staff status updated to ${targetStatus}`);
      } else {
        error(response.message || 'Failed to update status');
      }
    } catch (err) {
      console.error('Error toggling status:', err);
      error('An error occurred while updating status');
    } finally {
      setIsStatusModalOpen(false);
      setStaffToToggle(null);
    }
  };

  const removeSubject = (index) => {
    const updated = formData.subjects?.filter((_, i) => i !== index) || [];
    setFormData({ ...formData, subjects: updated });
  };

  const updateSubject = (index, value) => {
    const updated = formData.subjects?.map((s, i) =>
      i === index ? { name: value } : s
    ) || [];
    setFormData({ ...formData, subjects: updated });
  };

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    if (!phone) return false;
    // Remove all non-digits
    const digits = phone.replace(/\D/g, '');
    // Indian numbers are usually 10 digits (excluding +91)
    // The phone state should already contain +91 if selected in PhoneInput
    return digits.length >= 10;
  };

  const validateAadhar = (aadhar) => {
    const cleanAadhar = aadhar.replace(/[\s-]/g, '');
    const aadharRegex = /^[2-9]{1}[0-9]{11}$/;
    return aadharRegex.test(cleanAadhar);
  };

  const validatePAN = (pan) => {
    const cleanPAN = pan.replace(/\s/g, '').toUpperCase();
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    return panRegex.test(cleanPAN);
  };

  const validateForm = () => {
    const errors = {};

    // Name validation
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    } else if (formData.name.trim().length < 3) {
      errors.name = 'Name must be at least 3 characters';
    }

    // Qualification validation
    const hasQualificationField = configFields.some(f => f.fieldName === 'qualification');
    const qualification = formData.qualification || formData.customFields?.qualification || '';
    if (hasQualificationField && !qualification.trim()) {
      errors.qualification = 'Qualification is required';
    } else if (qualification.trim() && qualification.trim().length < 2) {
      errors.qualification = 'Qualification must be at least 2 characters';
    }

    // Experience validation
    const isFieldRequired = (fieldName) => {
      const field = allConfigFields.find(f => f.fieldName === fieldName);
      if (field) {
        return field.required === true || field.validation?.required === true;
      }
      return true; // Default to true
    };

    const hasExperienceField = configFields.some(f => f.fieldName === 'experienceInYears');
    const experience = formData.experienceInYears !== '' ? formData.experienceInYears : formData.customFields?.experienceInYears;
    if (hasExperienceField && (experience === '' || experience === null || experience === undefined)) {
      errors.experienceInYears = 'Experience is required';
    } else if (experience !== undefined && experience !== null && experience !== '' && (isNaN(experience) || parseFloat(experience) < 0)) {
      errors.experienceInYears = 'Please enter a valid experience in years';
    }

    // Email validation
    if (!formData.email.trim() && isFieldRequired('email')) {
      errors.email = 'Email is required';
    } else if (formData.email.trim() && !validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Phone validation
    if (!formData.phone.trim() && isFieldRequired('phone')) {
      errors.phone = 'Phone number is required';
    } else if (formData.phone.trim() && !validatePhone(formData.phone)) {
      errors.phone = 'Please enter a valid 10-digit Indian phone number';
    }

    // Academic year validation
    if (!formData.academicYear && isFieldRequired('academicYear')) {
      errors.academicYear = 'Academic year is required';
    }

    // Class validation - Only for Teaching Staff
    const isTeachingStaff = designations.find(d => d.name === formData.designation)?.fieldType === 'teaching';
    if (isTeachingStaff && !formData.inChargeOf && isFieldRequired('inChargeOf')) {
      errors.inChargeOf = 'Class selection is required';
    }

    // Date of birth validation
    if (!formData.dateOfBirth && isFieldRequired('dateOfBirth')) {
      errors.dateOfBirth = 'Date of birth is required';
    } else if (formData.dateOfBirth) {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      if (birthDate >= today) {
        errors.dateOfBirth = 'Date of birth must be in the past';
      }
    }

    // Gender validation
    if (!formData.gender && isFieldRequired('gender')) {
      errors.gender = 'Gender is required';
    }

    // Marital status validation
    if (!formData.maritalStatus && isFieldRequired('maritalStatus')) {
      errors.maritalStatus = 'Marital status is required';
    }

    // Mother tongue validation
    if (!formData.motherTongue.trim() && isFieldRequired('motherTongue')) {
      errors.motherTongue = 'Mother tongue is required';
    }

    // Nationality validation
    if (!formData.nationality.trim() && isFieldRequired('nationality')) {
      errors.nationality = 'Nationality is required';
    }

    // Blood group validation
    if (!formData.bloodGroup && isFieldRequired('bloodGroup')) {
      errors.bloodGroup = 'Blood group is required';
    }

    // Aadhar validation
    if (!formData.aadharNumber.trim() && isFieldRequired('aadharNumber')) {
      errors.aadharNumber = 'Aadhar number is required';
    } else if (formData.aadharNumber.trim() && !validateAadhar(formData.aadharNumber)) {
      errors.aadharNumber = 'Please enter a valid 12-digit Aadhar number';
    }

    // PAN validation
    if (!formData.pan.trim() && isFieldRequired('pan')) {
      errors.pan = 'PAN is required';
    } else if (formData.pan.trim() && !validatePAN(formData.pan)) {
      errors.pan = 'Please enter a valid PAN (e.g., ABCDE1234F)';
    }

    // Address validation
    if (!formData.address.trim() && isFieldRequired('address')) {
      errors.address = 'Address is required';
    }

    // Salary validation
    if (!formData.salary.trim() && isFieldRequired('salary')) {
      errors.salary = 'Salary is required';
    } else if (formData.salary.trim() && (isNaN(formData.salary) || parseFloat(formData.salary) < 0)) {
      errors.salary = 'Please enter a valid amount';
    }

    // Contact person validation
    if ((!formData.contactPerson || formData.contactPerson.length === 0) && isFieldRequired('emergencyContacts')) {
      errors.contactPerson = 'At least one contact person is required';
    } else if (formData.contactPerson && formData.contactPerson.length > 0) {
      formData.contactPerson.forEach((contact, index) => {
        // If a row is partially filled or required, validate its fields.
        // If emergencyContacts isn't required and the row is completely empty, we can skip it.
        const isRowEmpty = !contact.name.trim() && !contact.relation.trim() && !contact.phone.trim();
        if (!isRowEmpty || isFieldRequired('emergencyContacts')) {
          if (!contact.name.trim()) {
            errors[`contactPerson_${index}_name`] = 'Contact person name is required';
          }
          if (!contact.relation.trim()) {
            errors[`contactPerson_${index}_relation`] = 'Relation is required';
          }
          if (!contact.phone.trim()) {
            errors[`contactPerson_${index}_phone`] = 'Contact person phone is required';
          } else if (!validatePhone(contact.phone)) {
            errors[`contactPerson_${index}_phone`] = 'Please enter a valid 10-digit Indian phone number';
          }
        }
      });
    }

    // Subjects validation
    if ((!formData.subjects || formData.subjects.length === 0) && isFieldRequired('subjects')) {
      errors.subjects = 'At least one subject is required';
    } else if (formData.subjects && formData.subjects.length > 0) {
      formData.subjects.forEach((subject, index) => {
        const isRowEmpty = !subject.name.trim();
        if (!isRowEmpty || isFieldRequired('subjects')) {
          if (!subject.name.trim()) {
            errors[`subject_${index}`] = 'Subject name is required';
          }
        }
      });
    }

    // Custom fields validation
    configFields.forEach(field => {
      const fieldName = field.fieldName || field.name;
      // Skip fields handled manually
      if (['qualification', 'experienceInYears'].includes(fieldName)) return;
      
      const isRequired = field.required === true || field.validation?.required === true;
      if (isRequired) {
        const value = formData.customFields?.[fieldName];
        if (value === undefined || value === null || String(value).trim() === '') {
          errors[fieldName] = `${field.fieldLabel || field.label || fieldName} is required`;
        }
      }
    });

    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      console.log('Validation errors:', errors);
      console.log('Form data:', formData);
    }
    return Object.keys(errors).length === 0;
  };

  const getClassIncharged = (teacherId) => {
    const inchargedClass = classes.find(cls => cls.classIncharge === teacherId);
    return inchargedClass ? `${inchargedClass.name} - Section ${inchargedClass.section}` : null;
  };

  const columns = [
    {
      key: 'name',
      label: 'Staff Member',
      render: (teacher) => (
        <div className="flex items-center gap-4 py-2">
          <StaffAvatar teacher={teacher} />
          <div className="flex flex-col">
            <span className="font-bold text-[#1e293b] text-base group-hover:text-[#1B6CA8] transition-colors">
              {teacher.name}
            </span>
            <span className="text-sm text-slate-500 font-medium">{teacher.email}</span>
          </div>
        </div>
      )
    },
    {
      key: 'designation',
      label: 'Role',
      render: (teacher) => (
        <div className="flex flex-col gap-1">
          <span className="px-3 py-1 bg-blue-50 text-[#1B6CA8] text-xs rounded-xl font-bold border border-blue-100/50 inline-flex items-center gap-1.5 w-fit">
            <div className="w-1.5 h-1.5 rounded-full bg-[#1B6CA8]"></div>
            {teacher.designation || 'Not specified'}
          </span>
        </div>
      )
    },
    {
      key: 'qualification',
      label: 'Education & Exp.',
      render: (teacher) => (
        <div className="flex flex-col">
          <span className="text-sm font-bold text-[#1e293b]">{teacher.qualification}</span>
          <span className="text-xs text-slate-500 font-bold uppercase tracking-wider mt-0.5">
            {teacher?.experienceInYears || 0} Years Experience
          </span>
        </div>
      )
    },
    {
      key: 'employeeId',
      label: 'Employee ID',
      render: (teacher) => (
        <div className="flex flex-col gap-1">
          <span className="px-3 py-1 bg-slate-100 text-slate-600 text-xs rounded-xl font-bold border border-slate-200/50 inline-flex items-center gap-1.5 w-fit">
            {teacher.employeeId || 'N/A'}
          </span>
          {teacher.employeeNo && (
            <span className="text-[10px] text-blue-600 font-bold uppercase tracking-wider">
              Bio: {teacher.employeeNo}
            </span>
          )}
        </div>
      )
    },
    {
      key: 'classIncharged',
      label: 'In-Charge Of',
      render: (teacher) => {
        const inchargedClass = getClassIncharged(teacher._id);
        return inchargedClass ? (
          <span className="px-3 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-xl font-bold border border-emerald-100/50 inline-flex items-center gap-1.5">
            <GraduationCap size={14} />
            {inchargedClass}
          </span>
        ) : (
          <span className="text-slate-400 text-xs font-bold italic">No Assigned Class</span>
        );
      }
    },
    {
      key: 'status',
      label: 'Status',
      render: (teacher) => (
        <button
          onClick={() => {
            setStaffToToggle(teacher);
            setTargetStatus(teacher.status === 'inactive' ? 'active' : 'inactive');
            setIsStatusModalOpen(true);
          }}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
            teacher.status === 'active' || !teacher.status ? 'bg-green-500' : 'bg-slate-300'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
              teacher.status === 'active' || !teacher.status ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      )
    },
    {
      key: 'actions',
      label: 'Management',
      render: (teacher) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleView(teacher)}
            className="p-2.5 bg-slate-50 text-slate-500 hover:bg-blue-50 hover:text-[#1B6CA8] rounded-xl transition-all duration-300 group"
            title="View Profile"
          >
            <Eye size={18} className="group-hover:scale-110 transition-transform" />
          </button>
          <button
            onClick={() => handleEdit(teacher)}
            className="p-2.5 bg-slate-50 text-slate-500 hover:bg-teal-50 hover:text-[#3AA8A7] rounded-xl transition-all duration-300 group"
            title="Edit Member"
          >
            <Edit2 size={18} className="group-hover:scale-110 transition-transform" />
          </button>
          <button
            onClick={() => handleDelete(teacher._id)}
            className="p-2.5 bg-slate-50 text-slate-500 hover:bg-red-50 hover:text-red-500 rounded-xl transition-all duration-300 group"
            title="Remove Member"
          >
            <Trash2 size={18} className="group-hover:scale-110 transition-transform" />
          </button>
        </div>
      )
    }
  ];

  // Loading state
  if (isCreating) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // List View
  if (viewMode === 'list') {
    return (
      <div className="space-y-8 animate-fadeIn max-w-[1600px] mx-auto">
        {/* Modern Header Section */}
        <div className="glass-card rounded-[2.5rem] p-8 px-10 flex flex-col md:flex-row justify-between items-center gap-6 premium-shadow relative z-50">
          <div className="text-center md:text-left">
            <h1 className="text-4xl font-black tracking-tight text-[#1e293b]">
              <span className="bg-gradient-to-r from-blue-600 to-teal-500 bg-clip-text text-transparent">Staff</span>
            </h1>
            <p className="text-slate-500 mt-2 font-medium flex items-center justify-center md:justify-start gap-2">
              <Users size={18} className="text-[#1B6CA8]" />
              Manage your academic and administrative team
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            <div className="flex bg-slate-100 rounded-2xl p-1 shadow-sm">
              <button
                onClick={() => {
                  const columns = [
                    { header: 'Name', dataKey: 'name' },
                    { header: 'Designation', dataKey: 'designation' },
                    { header: 'Email', dataKey: 'email' },
                    { header: 'Phone', dataKey: 'phone' },
                    { header: 'Employee ID', dataKey: 'employeeId' }
                  ];
                  ReportService.generatePDF('Staff Directory', columns, filteredTeachers, 'staff_directory.pdf');
                }}
                className="px-4 py-2.5 text-[#1e293b] hover:bg-white rounded-xl transition-all duration-200 flex items-center gap-2 font-bold text-sm"
              >
                <Download size={16} className="text-blue-600" />
                <span>PDF</span>
              </button>
              <button
                onClick={() => {
                  const data = filteredTeachers.map(t => ({
                    Name: t.name,
                    Designation: t.designation,
                    Email: t.email,
                    Phone: t.phone,
                    'Employee ID': t.employeeId,
                    Qualification: t.qualification,
                    Experience: t.experienceInYears
                  }));
                  ReportService.generateExcel(data, 'staff_directory.xlsx');
                }}
                className="px-4 py-2.5 text-[#1e293b] hover:bg-white rounded-xl transition-all duration-200 flex items-center gap-2 font-bold text-sm"
              >
                <Download size={16} className="text-emerald-600" />
                <span>Excel</span>
              </button>
            </div>
            <button
              onClick={() => setIsDesignationModalOpen(true)}
              className="px-6 py-3.5 bg-slate-100 text-[#1e293b] rounded-2xl hover:bg-slate-200 transition-all duration-300 flex items-center gap-3 font-bold group shadow-sm"
              title="Manage Designations"
            >
              <div className="p-2 bg-white rounded-xl shadow-sm group-hover:scale-110 transition-transform">
                <Settings size={20} className="text-[#1B6CA8]" />
              </div>
              <span>Designations</span>
            </button>
            <button
              onClick={() => {
                resetForm();
                setIsModalOpen(true);
              }}
              className="primary-gradient-bg text-white px-8 py-3.5 rounded-2xl hover:opacity-90 transition-all duration-300 flex items-center gap-3 font-bold shadow-lg shadow-blue-200/50 hover:scale-[1.02] active:scale-[0.98]"
            >
              <Plus size={24} />
              <span>Add New Staff</span>
            </button>
          </div>
        </div>




        {/* Filters */}
        <div className="glass-card rounded-[2rem] p-8 premium-shadow border-none animate-slideInUp relative z-0">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-8 gap-4">
            <div>
              <h3 className="text-xl font-black text-slate-800 tracking-tight">Filters</h3>
              <p className="text-slate-400 text-sm font-medium">Refine your staff directory view</p>
            </div>
            <div className="px-4 py-2 bg-[#1B6CA8]/5 rounded-full">
              <span className="text-sm font-bold text-[#1B6CA8]">
                {filteredTeachers?.length || 0} <span className="text-slate-400 font-medium">of</span> {teachers?.length || 0} <span className="text-slate-400 font-medium">staff</span>
              </span>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
            {/* Search Filter */}
            <div className="flex flex-col">
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Search Name/Email</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50/50 border border-slate-100 rounded-xl focus:ring-4 focus:ring-[#1B6CA8]/10 focus:border-[#1B6CA8] outline-none transition-all duration-300 font-semibold text-slate-700"
                />
              </div>
            </div>

            {/* Academic Year Filter */}
            <div className="flex flex-col">
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Academic Year</label>
              <select
                value={selectedAcademicYearFilter}
                onChange={(e) => setSelectedAcademicYearFilter(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50/50 border border-slate-100 rounded-xl focus:ring-4 focus:ring-[#1B6CA8]/10 focus:border-[#1B6CA8] outline-none transition-all duration-300 font-semibold text-slate-700 cursor-pointer"
              >
                <option value="">All Years</option>
                {getUniqueAcademicYears().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            {/* Class Incharge Of Filter */}
            <div className="flex flex-col">
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Incharge of Class</label>
              <SearchableDropdown
                options={getClassesForSelectedAcademicYear()}
                value={getClassesForSelectedAcademicYear().find(option => option._id === selectedClassInchargeOfFilter) || ''}
                onChange={(selectedClass) => setSelectedClassInchargeOfFilter(selectedClass?._id || '')}
                placeholder={selectedAcademicYearFilter ? "Select Class" : "Select Academic Year First"}
                placeholderClassName="text-[10px]"
                label=""
                searchPlaceholder="Search classes..."
                noResultsText="No classes found"
                disabled={!selectedAcademicYearFilter}
              />
            </div>
            <div>
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Designation</label>
              <select
                value={selectedDesignationFilter}
                onChange={(e) => setSelectedDesignationFilter(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50/50 border border-slate-100 rounded-xl focus:ring-4 focus:ring-[#1B6CA8]/10 focus:border-[#1B6CA8] outline-none transition-all duration-300 font-semibold text-slate-700 cursor-pointer"
              >
                <option value="">All Designations</option>
                {Array.isArray(designations) && designations.map((designation) => (
                  <option key={designation._id} value={designation.name}>
                    {designation.name}
                  </option>
                ))}
              </select>
            </div>


            {/* Class Incharge Filter */}
            <div className="flex flex-col">
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Class Incharge</label>
              <select
                value={selectedClassInchargeFilter}
                onChange={(e) => setSelectedClassInchargeFilter(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50/50 border border-slate-100 rounded-xl focus:ring-4 focus:ring-[#1B6CA8]/10 focus:border-[#1B6CA8] outline-none transition-all duration-300 font-semibold text-slate-700 cursor-pointer"
              >
                <option value="">All status</option>
                <option value="yes">Class Incharge</option>
                <option value="no">Not Class Incharge</option>
              </select>
            </div>

            {/* Gender Filter */}
            <div className="flex flex-col">
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Gender</label>
              <select
                value={selectedGenderFilter}
                onChange={(e) => setSelectedGenderFilter(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50/50 border border-slate-100 rounded-xl focus:ring-4 focus:ring-[#1B6CA8]/10 focus:border-[#1B6CA8] outline-none transition-all duration-300 font-semibold text-slate-700 cursor-pointer"
              >
                <option value="">All Genders</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Name Sort Filter */}
            <div className="flex flex-col">
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Sort Order</label>
              <select
                value={nameSort}
                onChange={(e) => setNameSort(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50/50 border border-slate-100 rounded-xl focus:ring-4 focus:ring-[#1B6CA8]/10 focus:border-[#1B6CA8] outline-none transition-all duration-300 font-semibold text-slate-700 cursor-pointer"
              >
                <option value="a-z">Name (A to Z)</option>
                <option value="z-a">Name (Z to A)</option>
              </select>
            </div>

            {/* Clear Filters */}
            <div className="flex flex-col justify-end xl:col-start-6">
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedClassInchargeFilter('');
                  setSelectedClassInchargeOfFilter('');
                  setSelectedGenderFilter('');
                  setSelectedDesignationFilter('');
                  setSelectedAcademicYearFilter(() => {
                    const currentYear = new Date().getFullYear();
                    return `${currentYear}-${currentYear + 1}`;
                  });
                  setNameSort('a-z');
                }}
                className="w-full px-4 py-3 text-slate-500 font-bold bg-slate-100/50 rounded-xl hover:bg-slate-200/50 transition-all duration-300 flex items-center justify-center gap-2"
              >
                <X size={18} />
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        <div className="flex justify-end mb-4">
          <ItemsPerPageSelector 
            value={itemsPerPage} 
            onChange={handleItemsPerPageChange} 
          />
        </div>

        <Table
          data={paginatedTeachers}
          columns={columns}
          searchable={false}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          emptyMessage="No teachers found"
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          totalItems={filteredTeachers.length}
        />

        <ConfirmModal
          isOpen={isStatusModalOpen}
          onClose={() => {
            setIsStatusModalOpen(false);
            setStaffToToggle(null);
          }}
          onConfirm={handleStatusToggle}
          title="Change Staff Status"
          message={`Are you sure you want to change this staff member's status to ${targetStatus}? This will ${targetStatus === 'inactive' ? 'restrict' : 'restore'} their access.`}
          confirmText="Yes, Change Status"
          cancelText="No, Keep as is"
          type={targetStatus === 'inactive' ? 'danger' : 'warning'}
        />

        {/* Add Teacher Modal */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => {
            if (hasUnsavedChanges()) {
              setPendingAction('close');
              setShowUnsavedChangesModal(true);
            } else {
              setIsModalOpen(false);
              resetForm();
            }
          }}
          title="Add New Staff"
          size="xl"
          preventBackdropClose={true}
        >
          <form onSubmit={handleSubmit} className="space-y-8 p-1">
            <div className="bg-emerald-50/50 p-6 rounded-3xl border border-emerald-100/50">
              <label className="block text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-3 ml-1">Select Designation *</label>
              <select
                value={formData.designation}
                onChange={(e) => setFormData({ ...formData, designation: e.target.value, customFields: {} })}
                className={`w-full px-5 py-4 bg-white border-2 rounded-2xl focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none transition-all text-lg font-semibold text-slate-700 shadow-sm ${validationErrors.designation ? 'border-red-400' : 'border-emerald-100'
                  }`}
                required
              >
                <option value="">-- Choose Designation to Begin --</option>
                {Array.isArray(designations) && designations.map((designation) => (
                  <option key={designation._id} value={designation.name}>
                    {designation.name}
                  </option>
                ))}
              </select>
              {validationErrors.designation && (
                <p className="mt-2 text-sm text-red-600 font-medium ml-2">{validationErrors.designation}</p>
              )}
            </div>

            {formData.designation ? (
              <div className="space-y-8 animate-fadeIn">
                {/* Image Upload */}
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="w-24 h-24 bg-blue-50 rounded-2xl border-2 border-dashed border-blue-200 flex items-center justify-center overflow-hidden flex-shrink-0 relative group">
                    {imagePreview ? (
                      <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                    ) : formData.image && typeof formData.image === 'string' ? (
                      <img src={`/${formData.image}`} alt="Current" className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-blue-400 flex flex-col items-center">
                        <Plus size={24} />
                        <span className="text-[10px] font-bold uppercase mt-1">Photo</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-blue-600/10 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                  </div>
                  <div className="flex-1 w-full">
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Profile Image</label>
                    <div className="relative">
                      <input
                        type="file"
                        accept=".png,.jpg,.jpeg"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setFormData({ ...formData, image: file });
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              setImagePreview(reader.result);
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        className="hidden"
                        id="staff-image-upload"
                      />
                      <label
                        htmlFor="staff-image-upload"
                        className="inline-flex items-center px-4 py-2.5 bg-white border-2 border-slate-100 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-50 hover:border-blue-400 hover:text-blue-600 transition-all duration-300 cursor-pointer shadow-sm"
                      >
                        <Plus size={18} className="mr-2" />
                        Choose Photo
                      </label>
                      <p className="text-[11px] font-medium text-slate-400 mt-2 italic">
                        Recommended: Square JPG/PNG, Max 10MB
                      </p>
                    </div>
                  </div>
                </div>

                {/* Basic Information */}
                {/* Redundant Name field removed - Name is handled in Full Name or Dynamic Fields */}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Full Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter full name"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none ${validationErrors.name ? 'border-red-500' : 'border-gray-300'
                        }`}
                      required
                    />
                    {validationErrors.name && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Email Address *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="name@example.com"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none ${validationErrors.email ? 'border-red-500' : 'border-gray-300'
                        }`}
                      required
                    />
                    {validationErrors.email && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.email}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <PhoneInput
                      label="Phone Number"
                      value={formData.phone}
                      onChange={(val) => setFormData({ ...formData, phone: val })}
                      error={validationErrors.phone}
                      required
                    />
                  </div>
                  {/* Academic Fields - Only for Teaching Staff */}
                  {designations.find(d => d.name === formData.designation)?.fieldType === 'teaching' && (
                    <>
                      <div>
                          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Academic Year *</label>
                        <select
                          value={formData.academicYear}
                          onChange={(e) => {
                            setFormData({ ...formData, academicYear: e.target.value, class: '' });
                          }}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none ${validationErrors.academicYear ? 'border-red-500' : 'border-gray-300'
                            }`}
                          required
                        >
                          <option value="">Select Academic Year</option>
                          {getUniqueAcademicYears().map(year => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                        {validationErrors.academicYear && (
                          <p className="mt-1 text-sm text-red-600">{validationErrors.academicYear}</p>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Class Assignment - Only for Teaching Staff */}
                {designations.find(d => d.name === formData.designation)?.fieldType === 'teaching' && (
                  <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                    <div>
                      <SearchableDropdown
                        options={classOptions}
                        value={classOptions.find(option => option._id === formData.inChargeOf?._id) || ''}
                        onChange={(selectedClass) => setFormData({ ...formData, inChargeOf: selectedClass })}
                        placeholder={formData.academicYear ? "Select a class" : "First select academic year"}
                        label="In-Charge Of"
                        searchPlaceholder="Search classes..."
                        noResultsText="No classes found"
                        disabled={!formData.academicYear}
                      />
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <CustomDatePicker
                      label="Date of Birth"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                      required={true}
                      validationError={validationErrors.dateOfBirth}
                      maxYear={new Date().getFullYear()}
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Gender *</label>
                    <select
                      value={formData.gender}
                      onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none ${validationErrors.gender ? 'border-red-500' : 'border-gray-300'
                        }`}
                      required
                    >
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                    {validationErrors.gender && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.gender}</p>
                    )}
                  </div>
                </div>

                {/* Dynamic Custom Fields (Qualification, Experience, etc.) */}
                {configFields.length > 0 && (
                  <div className="bg-slate-50/50 p-6 rounded-[2rem] border border-slate-100 my-4">
                    <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Additional Professional Details</h3>
                    <DynamicFields
                      fields={configFields}
                      values={formData.customFields}
                      onChange={handleCustomFieldChange}
                      errors={validationErrors}
                    />
                  </div>
                )}

                {/* Personal Details Group */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Marital Status *</label>
                    <select
                      value={formData.maritalStatus}
                      onChange={(e) => setFormData({ ...formData, maritalStatus: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none ${validationErrors.maritalStatus ? 'border-red-500' : 'border-gray-300'
                        }`}
                      required
                    >
                      <option value="single">Single</option>
                      <option value="married">Married</option>
                      <option value="divorced">Divorced</option>
                      <option value="widowed">Widowed</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Mother Tongue *</label>
                    <input
                      type="text"
                      value={formData.motherTongue}
                      onChange={(e) => setFormData({ ...formData, motherTongue: e.target.value })}
                      placeholder="Enter mother tongue"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none ${validationErrors.motherTongue ? 'border-red-500' : 'border-gray-300'
                        }`}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Nationality *</label>
                    <input
                      type="text"
                      value={formData.nationality}
                      onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                      placeholder="Enter nationality"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none ${validationErrors.nationality ? 'border-red-500' : 'border-gray-300'
                        }`}
                      required
                    />
                  </div>
                </div>

                {/* Legal & Health Info */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Aadhar Number *</label>
                    <input
                      type="text"
                      value={formData.aadharNumber}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^[0-9\s-]*$/.test(value) && value.length <= 14) {
                          setFormData({ ...formData, aadharNumber: value });
                        }
                      }}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none ${validationErrors.aadharNumber ? 'border-red-500' : 'border-gray-300'
                        }`}
                      placeholder="1234 5678 9012"
                      maxLength="14"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">PAN *</label>
                    <input
                      type="text"
                      value={formData.pan}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        if (/^[A-Z0-9]*$/.test(value) && value.length <= 10) {
                          setFormData({ ...formData, pan: value });
                        }
                      }}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none ${validationErrors.pan ? 'border-red-500' : 'border-gray-300'
                        }`}
                      placeholder="ABCDE1234F"
                      maxLength="10"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Blood Group *</label>
                    <select
                      value={formData.bloodGroup}
                      onChange={(e) => setFormData({ ...formData, bloodGroup: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none ${validationErrors.bloodGroup ? 'border-red-500' : 'border-gray-300'
                        }`}
                      required
                    >
                      <option value="">Select</option>
                      <option value="A+">A+</option>
                      <option value="A-">A-</option>
                      <option value="B+">B+</option>
                      <option value="B-">B-</option>
                      <option value="AB+">AB+</option>
                      <option value="AB-">AB-</option>
                      <option value="O+">O+</option>
                      <option value="O-">O-</option>
                    </select>
                  </div>
                </div>

                <div className="mt-2">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Address</label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="44, roadName, city, state, pincode"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none ${validationErrors.address ? 'border-red-500' : 'border-gray-300'
                      }`}
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Salary (₹) *</label>
                    <input
                      type="number"
                      value={formData.salary}
                      onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none ${validationErrors.salary ? 'border-red-500' : 'border-gray-300'
                        }`}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                </div>

                <div className="border-t border-gray-100 pt-6 mt-6">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em]">Emergency Contacts</h3>
                      <p className="text-[10px] text-slate-400 mt-0.5">Secondary contact persons for safety</p>
                    </div>
                    <button
                      type="button"
                      onClick={addContactPerson}
                      className="px-4 py-2 bg-emerald-50 text-emerald-600 rounded-xl hover:bg-emerald-100 transition-all font-bold text-xs flex items-center gap-1.5"
                    >
                      <Plus size={14} />
                      Add Contact
                    </button>
                  </div>
                  {formData.contactPerson?.map((contact, index) => (
                    <div key={index} className="relative bg-teal-50/30 p-6 rounded-[2rem] border border-teal-100 mb-4 transition-all duration-300 hover:shadow-sm">
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-y-6 gap-x-8 items-start">
                        <div className="md:col-span-4">
                          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Full Name</label>
                          <input
                            type="text"
                            placeholder="e.g. John Doe"
                            value={contact.name ?? ''}
                            onChange={(e) => updateContactPerson(index, 'name', e.target.value)}
                            className={`w-full px-4 py-3 bg-white border rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all ${
                              validationErrors.contactPerson?.[index]?.name ? 'border-red-500' : 'border-teal-100'
                            }`}
                          />
                          {validationErrors.contactPerson?.[index]?.name && (
                            <p className="mt-1 text-xs text-red-500 ml-1">{validationErrors.contactPerson[index].name}</p>
                          )}
                        </div>

                        <div className="md:col-span-3">
                          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Relation</label>
                          <select
                            value={contact.relation ?? ''}
                            onChange={(e) => updateContactPerson(index, 'relation', e.target.value)}
                            className={`w-full px-4 py-3 bg-white border rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all cursor-pointer ${
                              validationErrors.contactPerson?.[index]?.relation ? 'border-red-500' : 'border-teal-100'
                            }`}
                          >
                            <option value="">Select Relation</option>
                            <option value="Father">Father</option>
                            <option value="Mother">Mother</option>
                            <option value="Brother">Brother</option>
                            <option value="Sister">Sister</option>
                            <option value="Grandfather">Grandfather</option>
                            <option value="Grandmother">Grandmother</option>
                            <option value="Guardian">Guardian</option>
                          </select>
                        </div>

                        <div className="md:col-span-4">
                          <PhoneInput
                            label="Phone"
                            value={contact.phone ?? ''}
                            onChange={(val) => updateContactPerson(index, 'phone', val)}
                            error={validationErrors.contactPerson?.[index]?.phone}
                          />
                        </div>

                        <div className="md:col-span-1 flex justify-center pt-8">
                          {formData.contactPerson.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeContactPerson(index)}
                              className="p-2.5 bg-white border border-teal-100 text-teal-400 hover:text-red-500 hover:border-red-100 hover:bg-red-50 rounded-full transition-all duration-300 shadow-sm"
                            >
                              <X size={18} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {validationErrors.contactPerson && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.contactPerson}</p>
                  )}
                </div>

                {/* Login Credentials Section */}
                <div className="p-6 bg-slate-50 rounded-[2rem] border border-slate-100 space-y-4 mb-6">
                  <div className="flex justify-between items-center">
                    <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em]">Mobile Login Credentials</h4>
                    <button
                      type="button"
                      onClick={generatePassword}
                      className="text-xs font-bold text-blue-600 hover:text-blue-700 bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-100 transition-all hover:scale-105 active:scale-95 flex items-center gap-2"
                    >
                      <Plus size={14} className="animate-spin-slow" />
                      Auto-generate Password
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Login ID (Employee ID)</label>
                      <input
                        type="text"
                        value={formData.employeeId || 'Auto-generated on save'}
                        disabled
                        className="w-full px-4 py-3 bg-slate-200/50 border border-slate-200 rounded-xl text-slate-500 font-bold cursor-not-allowed"
                      />
                      <p className="mt-1 text-[10px] text-slate-400 ml-1 italic">Staff use their Employee ID to log in.</p>
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Login Password *</label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          placeholder="Enter or generate password"
                          className={`w-full px-4 py-3 bg-white border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all pr-12 ${
                            validationErrors.password ? 'border-red-500' : 'border-slate-200'
                          }`}
                        />
                        {formData.password && (
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-blue-600 transition-colors"
                          >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        )}
                      </div>
                      {validationErrors.password && (
                        <p className="mt-1 text-xs text-red-500 ml-1">{validationErrors.password}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Biometric Employee No (Optional)</label>
                    <input
                      type="text"
                      value={formData.employeeNo || ''}
                      onChange={(e) => setFormData({ ...formData, employeeNo: e.target.value })}
                      placeholder="e.g. STAFF001"
                      className="w-full md:w-1/2 px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                    />
                    <p className="mt-1 text-[10px] text-slate-400 ml-1 italic">Map this to the ID on the Hikvision device to sync attendance automatically.</p>
                  </div>
                </div>

                <div className="border-t border-gray-100 pt-6 mt-6">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em]">Subjects</h3>
                      <p className="text-[10px] text-slate-400 mt-0.5">Add subjects this staff member teaches</p>
                    </div>
                    <button
                      type="button"
                      onClick={addSubject}
                      className="px-4 py-2 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-all font-bold text-xs flex items-center gap-1.5"
                    >
                      <Plus size={14} />
                      Add Subject
                    </button>
                  </div>
                  {formData.subjects?.map((subject, index) => (
                    <div key={index} className="flex gap-2 mb-3">
                      <input
                        type="text"
                        placeholder="Subject name"
                        value={subject.name ?? ''}
                        onChange={(e) => updateSubject(index, e.target.value)}
                        className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none ${validationErrors[`subject_${index}`] ? 'border-red-500' : 'border-gray-300'
                          }`}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => removeSubject(index)}
                        className="flex items-center justify-center w-10 h-10 text-red-400 hover:text-red-600 hover:bg-red-50 transition-all duration-200 rounded-xl group"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 px-6 bg-slate-50 rounded-[2.5rem] border-2 border-dashed border-slate-200">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
                  <Plus size={40} className="text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">Awaiting Selection</h3>
                <p className="text-slate-500 text-center max-w-sm">
                  Please select a staff designation above to unlock the onboarding form and configure their profile.
                </p>
              </div>
            )}

            <div className="flex justify-end pt-6 border-t border-gray-100 gap-3">
              <button
                type="button"
                onClick={() => {
                  if (hasUnsavedChanges()) {
                    setPendingAction('cancel');
                    setShowUnsavedChangesModal(true);
                  } else {
                    setIsModalOpen(false);
                    resetForm();
                  }
                }}
                className="px-6 py-2.5 text-slate-600 font-bold hover:bg-slate-50 rounded-xl transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isCreating}
                className={`px-8 py-2.5 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-200 active:scale-95 flex items-center gap-2 ${isCreating ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isCreating ? (
                  <>
                    <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Creating...</span>
                  </>
                ) : (
                  'Create Staff'
                )}
              </button>
            </div>
          </form>
        </Modal>

        {/* Unsaved Changes Modal */}
        <Modal
          isOpen={showUnsavedChangesModal}
          onClose={() => setShowUnsavedChangesModal(false)}
          title="Unsaved Changes"
          size="md"
        >
          <div className="p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">You have unsaved changes</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Are you sure you want to leave? Your changes will be lost.
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowUnsavedChangesModal(false);
                  setPendingAction(null);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Continue Editing
              </button>
              <button
                onClick={() => {
                  setShowUnsavedChangesModal(false);
                  if (pendingAction === 'close' || pendingAction === 'cancel') {
                    setIsModalOpen(false);
                    resetForm();
                  } else if (pendingAction === 'back') {
                    setIsModalOpen(false);
                    resetForm();
                  }
                  setPendingAction(null);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Discard Changes
              </button>
            </div>
          </div>
        </Modal>

        {/* Designation Settings Modal */}
        <DesignationSettingsModal
          isOpen={isDesignationModalOpen}
          onClose={() => setIsDesignationModalOpen(false)}
          onDesignationsUpdate={async () => {
            // Reload designations when they change
            try {
              const response = await designationService.getAllDesignations(user.token);
              const data = Array.isArray(response) ? response : (response.data || response.designations || []);
              setDesignations(Array.isArray(data) ? data : []);
            } catch (err) {
              console.error('Error reloading designations:', err);
            }
          }}
        />
      </div>
    );
  }
};

export default Staff;